import { createTheme } from '@mui/material/styles';

// Define the retro/geek-inspired color palette
const retroColors = {
  // Primary colors (now using cyan/teal as primary)
  primary: {
    main: '#00CCCC', // Cyan/teal as main color
    light: '#33DDDD',
    dark: '#009999',
    contrastText: '#000000',
  },
  // Secondary colors (now using a different shade of cyan)
  secondary: {
    main: '#00AAAA', // Darker cyan for secondary color
    light: '#33BBBB',
    dark: '#007777',
    contrastText: '#000000',
  },
  // Background colors
  background: {
    default: '#0a0a0a', // Very dark gray, almost black
    paper: '#121212', // Dark gray for cards and surfaces
    terminal: '#000000', // Pure black for terminal-like elements
  },
  // Text colors (updated for better readability)
  text: {
    primary: '#00CCCC', // Cyan for primary text
    secondary: '#33DDDD', // Lighter cyan for secondary text
    disabled: '#006666', // Darker cyan for disabled text
  },
  // Error, warning, info, success colors with a retro twist
  error: {
    main: '#ff0000', // Bright red
    light: '#ff3333',
    dark: '#cc0000',
  },
  warning: {
    main: '#ffff00', // Bright yellow
    light: '#ffff33',
    dark: '#cccc00',
  },
  info: {
    main: '#00ffff', // Cyan
    light: '#33ffff',
    dark: '#00cccc',
  },
  success: {
    main: '#00CCCC', // Same as primary
    light: '#33DDDD',
    dark: '#009999',
  },
  // Additional custom colors
  custom: {
    terminalCyan: '#33DDDD', // Lighter cyan
    darkTerminal: '#001111', // Dark cyan background
    gridLines: '#001111', // Dark cyan grid lines
    glowPrimary: 'rgba(0, 204, 204, 0.3)', // Primary cyan glow with reduced opacity
    glowSecondary: 'rgba(0, 170, 170, 0.3)', // Secondary cyan glow with reduced opacity
    glowBright: 'rgba(0, 255, 255, 0.3)', // Bright cyan glow with reduced opacity
  },
};

// Create the retro theme
const retroTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: retroColors.primary,
    secondary: retroColors.secondary,
    error: retroColors.error,
    warning: retroColors.warning,
    info: retroColors.info,
    success: retroColors.success,
    background: {
      default: retroColors.background.default,
      paper: retroColors.background.paper,
    },
    text: {
      primary: retroColors.text.primary,
      secondary: retroColors.text.secondary,
      disabled: retroColors.text.disabled,
    },
  },
  typography: {
    fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
    h1: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
      fontWeight: 400,
    },
    h2: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
      fontWeight: 400,
    },
    h3: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
      fontWeight: 400,
    },
    h4: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
      fontWeight: 400,
    },
    h5: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
      fontWeight: 400,
    },
    h6: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
      fontWeight: 400,
    },
    body1: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
    },
    body2: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
    },
    button: {
      fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 0, // Square corners for a more retro look
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: retroColors.background.default,
          backgroundImage: 'linear-gradient(rgba(0, 17, 17, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 17, 17, 0.3) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: retroColors.background.default,
          },
          '&::-webkit-scrollbar-thumb': {
            background: retroColors.primary.main,
            borderRadius: 0,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: retroColors.background.paper,
          border: `1px solid ${retroColors.primary.main}`,
          boxShadow: `0 0 10px ${retroColors.custom.glowPrimary}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${retroColors.primary.main}`,
          boxShadow: `0 0 10px ${retroColors.custom.glowPrimary}`,
          backgroundColor: retroColors.background.terminal,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: 'uppercase',
          border: `1px solid ${retroColors.primary.main}`,
          '&:hover': {
            boxShadow: `0 0 10px ${retroColors.custom.glowPrimary}`,
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: `0 0 10px ${retroColors.custom.glowPrimary}`,
            color: '#FFFFFF', // White text on hover for better readability
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: retroColors.background.terminal,
          borderBottom: `1px solid ${retroColors.primary.main}`,
          boxShadow: `0 0 10px ${retroColors.custom.glowPrimary}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: retroColors.background.terminal,
          borderRight: `1px solid ${retroColors.primary.main}`,
          boxShadow: `0 0 10px ${retroColors.custom.glowPrimary}`,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: retroColors.background.terminal,
          border: `1px solid ${retroColors.primary.main}`,
          boxShadow: `0 0 5px ${retroColors.custom.glowPrimary}`,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            boxShadow: `0 0 10px ${retroColors.custom.glowPrimary}`,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${retroColors.primary.main}`,
        },
        indicator: {
          backgroundColor: retroColors.primary.main,
          height: 3,
          boxShadow: `0 0 5px ${retroColors.custom.glowPrimary}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          '&.Mui-selected': {
            color: retroColors.primary.main,
            fontWeight: 'bold',
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: retroColors.primary.main,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: retroColors.background.terminal,
          border: `1px solid ${retroColors.primary.main}`,
          boxShadow: `0 0 5px ${retroColors.custom.glowPrimary}`,
          color: retroColors.text.primary,
          fontFamily: '"Share Tech Mono", "Roboto Mono", monospace',
        },
        arrow: {
          color: retroColors.primary.main,
        },
      },
    },
  },
});

// Add custom properties to the theme
const augmentedTheme = {
  ...retroTheme,
  custom: retroColors.custom,
};

// Export the theme
export default augmentedTheme;
